import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Table, Button, Badge, Spinner, Alert, Form } from 'react-bootstrap';

import coldemailer_api from './coldemailer_api';
import coldemailer_config from './coldemailer_config';
import NDSelect from './components/NDSelect';
import './coldemailer.css';
import './coldemailer-styles.css';

const c_RowsPerPageOptions = [10, 25, 50, 100];
const c_DefaultRowsPerPage = 25;

// Matches the status enum on the EmailList model. An archived list refuses new members, so the
// state is worth being able to set from here rather than only in the database.
const c_StatusOptions = [
	{ value: 'inactive', label: 'Inactive' },
	{ value: 'active', label: 'Active' },
	{ value: 'archived', label: 'Archived' }
];

const c_VerificationVariants = {
	unverified: 'secondary',
	valid: 'success',
	invalid: 'danger',
	risky: 'warning'
};

const format_date = (value) => {
	if(!value) {
		return '-';
	}

	const date = new Date(value);
	if(isNaN(date.getTime())) {
		return '-';
	}

	return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const get_variant = (variants, value) => {
	if(value && variants[value]) {
		return variants[value];
	}

	return 'secondary';
};

function EmailListView() {
	const navigate = useNavigate();
	const { id } = useParams();

	const [email_list, set_email_list] = useState(null);
	const [members, set_members] = useState([]);
	const [loading, set_loading] = useState(true);
	const [error, set_error] = useState('');
	const [saving_status, set_saving_status] = useState(false);

	// The members endpoint returns the whole list in one response, so the search and paging below
	// work on what is already loaded rather than refetching
	const [search_text, set_search_text] = useState('');
	const [page, set_page] = useState(0);
	const [rows_per_page, set_rows_per_page] = useState(c_DefaultRowsPerPage);

	// Which members are ticked, held by id so a selection survives paging and searching. Selecting
	// covers the whole list rather than the page being looked at: the point of choosing companies
	// is to act on them, and having to walk eight pages to pick them all would be absurd.
	const [selected_member_ids, set_selected_member_ids] = useState([]);
	const [adding_recipients, set_adding_recipients] = useState(false);
	const [add_result, set_add_result] = useState('');

	// Turns the ticked companies into recipients the send path can pick up. A member is a company
	// on a list; a recipient is an address the sender works through, and until now there was no way
	// to get from one to the other.
	const on_add_as_recipients = async () => {
		if(!selected_member_ids.length) {
			return;
		}

		set_adding_recipients(true);
		set_error('');
		set_add_result('');

		try {
			const result = await coldemailer_api.add_members_as_recipients(id, selected_member_ids);

			// The add no longer says what became a recipient, because nothing has yet: each address
			// is verified first and only then queued. What it can say is how many went in.
			const parts = [];
			parts.push(result.queued_for_verification + ' queued for verification');

			if(result.already_in_progress) {
				parts.push(result.already_in_progress + ' already being verified');
			}

			if(result.skipped_invalid) {
				parts.push(result.skipped_invalid + ' with no address');
			}

			set_add_result(parts.join(', ') + '. Only addresses the provider confirms become recipients.');
			set_selected_member_ids([]);
		} catch (error) {
			console.log('EmailListView.on_add_as_recipients - ERROR: ', error);

			const response_data = error.response ? error.response.data : null;
			const server_message = response_data && response_data.message ? response_data.message : null;

			set_error(server_message ? server_message : 'Failed to add the selected companies as recipients.');
		} finally {
			set_adding_recipients(false);
		}
	};

	const load_list = async () => {
		set_loading(true);
		set_error('');

		try {
			const list = await coldemailer_api.get_email_list(id);
			set_email_list(list);

			const list_members = await coldemailer_api.get_email_list_members(id);
			set_members(list_members);
		} catch (error) {
			console.log('EmailListView.load_list - ERROR: ', error);
			set_error('Failed to load the email list.');
		} finally {
			set_loading(false);
		}
	};

	useEffect(function() {
		load_list();
	}, [id]);

	// The update endpoint writes whatever body it is given, so the list's existing values travel
	// with the new status rather than only the status, which would blank the rest.
	const on_status_change = async (event) => {
		const status = event.target.value;

		set_saving_status(true);
		set_error('');

		try {
			const updated = await coldemailer_api.update_email_list(id, {
				name: email_list.name,
				description: email_list.description,
				slug: email_list.slug,
				status,
				is_public: email_list.is_public,
				app_id: email_list.app_id,
				metadata: email_list.metadata
			});

			set_email_list(updated);
		} catch (error) {
			console.log('EmailListView.on_status_change - ERROR: ', error);
			set_error('Failed to update the status.');
		} finally {
			set_saving_status(false);
		}
	};

	const on_back = () => {
		navigate(coldemailer_config.campaign_route);
	};

	// A member holds the LeadDiscovery company it was added from, so the row opens that company
	const on_row_click = (member) => {
		if(!member.company_id) {
			return;
		}

		navigate(coldemailer_config.lead_discovery_route + '/' + member.company_id);
	};

	const render_header = () => {
		return (
			<div className="ce-header">
				<div>
					<h4 className="ce-title">
						{email_list && email_list.name ? email_list.name : 'Email List'}
					</h4>
					<p className="ce-subtitle">
						{email_list && email_list.description ? email_list.description : 'Companies on this list'}
					</p>
				</div>

				<div className="ce-header-actions">
					<Button
						size="sm"
						className="ce-button-create"
						onClick={on_add_as_recipients}
						disabled={adding_recipients || (selected_member_ids.length === 0)}
					>
						{adding_recipients ? 'Adding...' : 'Add As Recipients'}
					</Button>

					<Button variant="outline-light" size="sm" className="ce-button" onClick={load_list}>
						Refresh
					</Button>

					<Button variant="outline-light" size="sm" className="ce-button" onClick={on_back}>
						Back
					</Button>
				</div>
			</div>
		);
	};

	const render_summary = () => {
		if(!email_list) {
			return null;
		}

		return (
			<div className="ce-panel ce-detail-panel">
				<div className="ce-detail-grid">
					<div className="ce-detail-row">
						<div className="ce-detail-label">Status</div>
						<div className="ce-detail-value">
							<NDSelect
								size="sm"
								options={c_StatusOptions}
								value={email_list.status ? email_list.status : 'inactive'}
								onChange={on_status_change}
								disabled={saving_status}
							/>
						</div>
					</div>

					<div className="ce-detail-row">
						<div className="ce-detail-label">Members</div>
						<div className="ce-detail-value">{members.length}</div>
					</div>

					<div className="ce-detail-row">
						<div className="ce-detail-label">Slug</div>
						<div className="ce-detail-value">{email_list.slug ? email_list.slug : '-'}</div>
					</div>

					<div className="ce-detail-row">
						<div className="ce-detail-label">Created</div>
						<div className="ce-detail-value">{format_date(email_list.createdAt)}</div>
					</div>
				</div>
			</div>
		);
	};

	const render_empty_row = () => {
		return (
			<tr>
				<td colSpan={5} className="ce-empty">
					{search_text.trim() ? 'No companies match your search.' : 'No companies on this list yet.'}
				</td>
			</tr>
		);
	};

	const render_row = (member) => {
		return (
			<tr key={member.id} className="ce-row-clickable" onClick={function() { on_row_click(member); }}>
				<td
					className="ce-elm-col-select"
					onClick={function(event) { event.stopPropagation(); }}
				>
					<Form.Check
						type="checkbox"
						checked={is_member_selected(member.id)}
						onChange={function() { on_member_toggle(member.id); }}
					/>
				</td>
				<td className="ce-elm-col-company">
					{member.company_name ? member.company_name : '-'}
				</td>
				<td className="ce-elm-col-email">
					{member.email ? member.email : '-'}
				</td>
				<td className="ce-elm-col-verification">
					<Badge bg={get_variant(c_VerificationVariants, member.verification_status)}>
						{member.verification_status ? member.verification_status : 'unverified'}
					</Badge>
				</td>
				<td className="ce-elm-col-added">
					{format_date(member.createdAt)}
				</td>
			</tr>
		);
	};

	// Matches on company name or address, so either half of a row can be searched for
	const get_filtered_members = () => {
		const search = search_text.trim().toLowerCase();
		if(!search) {
			return members;
		}

		return members.filter(function(member) {
			const company_name = member.company_name ? member.company_name.toLowerCase() : '';
			const email = member.email ? member.email.toLowerCase() : '';

			return (company_name.indexOf(search) !== -1) || (email.indexOf(search) !== -1);
		});
	};

	const is_member_selected = (member_id) => {
		return selected_member_ids.indexOf(member_id) !== -1;
	};

	const on_member_toggle = (member_id) => {
		if(is_member_selected(member_id)) {
			set_selected_member_ids(selected_member_ids.filter(function(id) {
				return id !== member_id;
			}));

			return;
		}

		set_selected_member_ids(selected_member_ids.concat([member_id]));
	};

	// Every member on the list, not only the page in view and not only what the search left
	// showing. Ticking the box in the header means all of them.
	const on_select_all_toggle = () => {
		if(selected_member_ids.length === members.length) {
			set_selected_member_ids([]);
			return;
		}

		set_selected_member_ids(members.map(function(member) {
			return member.id;
		}));
	};

	const on_search_change = (event) => {
		set_search_text(event.target.value);
		set_page(0);
	};

	const on_rows_per_page_change = (event) => {
		set_rows_per_page(parseInt(event.target.value));
		set_page(0);
	};

	const render_members_toolbar = () => {
		return (
			<div className="ce-toolbar">
				<Form.Group className="ce-field ce-field-search">
					<Form.Label className="ce-label">Search</Form.Label>
					<Form.Control
						type="text"
						placeholder="Filter by company or email"
						value={search_text}
						onChange={on_search_change}
					/>
				</Form.Group>

				<Form.Group className="ce-field ce-field-rows">
					<Form.Label className="ce-label">Rows</Form.Label>
					<NDSelect
						options={c_RowsPerPageOptions.map(function(option) {
							return { value: option, label: String(option) };
						})}
						value={rows_per_page}
						onChange={on_rows_per_page_change}
					/>
				</Form.Group>

				<div className="ce-spacer"></div>
			</div>
		);
	};

	const render_members_pagination = (filtered_total) => {
		const total_pages = Math.max(1, Math.ceil(filtered_total / rows_per_page));
		const first_row = page * rows_per_page;
		const last_row = Math.min(first_row + rows_per_page, filtered_total);
		const showing_from = filtered_total === 0 ? 0 : first_row + 1;

		return (
			<div className="ce-pagination">
				<div className="ce-pagination-info">
					{'Showing ' + showing_from + ' to ' + last_row + ' of ' + filtered_total}
				</div>

				<div className="ce-pagination-controls">
					<Button
						variant="outline-light"
						size="sm" className="ce-button"
						disabled={page === 0}
						onClick={function() { set_page(page - 1); }}
					>
						Previous
					</Button>

					<span className="ce-page-label">
						{'Page ' + (page + 1) + ' of ' + total_pages}
					</span>

					<Button
						variant="outline-light"
						size="sm" className="ce-button"
						disabled={page >= total_pages - 1}
						onClick={function() { set_page(page + 1); }}
					>
						Next
					</Button>
				</div>
			</div>
		);
	};

	const render_members_panel = () => {
		const filtered_members = get_filtered_members();
		const first_row = page * rows_per_page;
		const page_members = filtered_members.slice(first_row, first_row + rows_per_page);

		return (
			<div className="ce-panel ce-members-panel">
				<div className="ce-panel-header">
					<h5 className="ce-panel-title">
						{'Companies (' + members.length + ')'}
					</h5>

					{/* A selection can span pages and survive a search, so the count says what is
					    actually ticked rather than leaving it to be worked out by paging around */}
					{selected_member_ids.length > 0 ? (
						<span className="ce-selection-count">
							{selected_member_ids.length + ' selected'}
						</span>
					) : null}
				</div>

				{render_members_toolbar()}

				<Table striped bordered hover responsive variant="dark" className="ce-table">
					<thead>
						<tr>
							<th className="ce-elm-col-select" style={{paddingLeft: "12px"}}>
								<Form.Check
									type="checkbox"
									checked={(members.length > 0) && (selected_member_ids.length === members.length)}
									onChange={on_select_all_toggle}
								/>
							</th>
							<th className="ce-elm-col-company">Company</th>
							<th className="ce-elm-col-email">Email</th>
							<th className="ce-elm-col-verification">Verification</th>
							<th className="ce-elm-col-added">Added</th>
						</tr>
					</thead>
					<tbody>
						{page_members.length === 0 ? render_empty_row() : page_members.map(render_row)}
					</tbody>
				</Table>

				{render_members_pagination(filtered_members.length)}
			</div>
		);
	};

	const render_body = () => {
		if(loading) {
			return (
				<div className="ce-loading">
					<Spinner animation="border" size="sm" />
				</div>
			);
		}

		if(!email_list) {
			return null;
		}

		return (
			<React.Fragment>
				{render_summary()}
				{render_members_panel()}
			</React.Fragment>
		);
	};

	// Set as a custom property so the host app's header height stays configurable
	const root_style = { '--ce-top-offset': coldemailer_config.top_offset_px + 'px' };

	return (
		<div className="ce-root ce-root-wide" style={root_style}>
			{render_header()}

			{error ? <Alert variant="danger">{error}</Alert> : null}

			{add_result ? <Alert variant="success">{add_result}</Alert> : null}

			{render_body()}
		</div>
	);
}

export default EmailListView;
